import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FeatureFlagsService } from '@principle-theorem/ng-feature-flags';
import {
  Patient,
  PatientMetadataDisplay,
} from '@principle-theorem/principle-core';
import {
  IPatient,
  IPatientMetadataDisplay,
  PatientMetadataDisplayTarget,
  PatientMetadataType,
  PatientMetadataValue,
  SYSTEM_METADATA_CONFIGURATIONS,
  SYSTEM_PATIENT_PROFILE_METADATA_DISPLAYS,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithRef,
  asyncForEach,
  snapshot,
  toInt,
} from '@principle-theorem/shared';
import { compact, isArray } from 'lodash';
import { Observable, ReplaySubject, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { GlobalStoreService } from '../../../store/global-store.service';
import { PatientMetadataDisplayComponent } from '../patient-metadata-display/patient-metadata-display.component';

interface IMetadataDisplayElement {
  display: IPatientMetadataDisplay;
  value?: PatientMetadataValue;
}

@Component({
  selector: 'pr-patient-metadata-display-outlet',
  templateUrl: './patient-metadata-display-outlet.component.html',
  styleUrls: ['./patient-metadata-display-outlet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, PatientMetadataDisplayComponent],
})
export class PatientMetadataDisplayOutletComponent {
  private _displayTarget$ = new ReplaySubject<PatientMetadataDisplayTarget>(1);
  private _patient$ = new ReplaySubject<IPatient>(1);
  elements$: Observable<IMetadataDisplayElement[]>;

  @Input()
  set displayTarget(displayTarget: PatientMetadataDisplayTarget) {
    if (displayTarget) {
      this._displayTarget$.next(displayTarget);
    }
  }

  @Input()
  set patient(patient: IPatient) {
    if (patient) {
      this._patient$.next(patient);
    }
  }

  constructor(
    globalStore: GlobalStoreService,
    private _featureFlags: FeatureFlagsService
  ) {
    const activeDisplays$ = combineLatest([
      this._displayTarget$,
      globalStore.patientMetadataDisplays$,
    ]).pipe(
      map(([displayTarget, displays]) =>
        PatientMetadataDisplay.filterByTarget(displays, displayTarget)
      )
    );
    this.elements$ = combineLatest([
      this._displayTarget$,
      activeDisplays$,
      this._patient$,
    ]).pipe(
      switchMap(([displayTarget, displays, patient]) =>
        this._toMetadataDisplayElements(displayTarget, displays, patient)
      )
    );
  }

  private async _toMetadataDisplayElements(
    displayTarget: PatientMetadataDisplayTarget,
    displays: WithRef<IPatientMetadataDisplay>[],
    patient: IPatient
  ): Promise<IMetadataDisplayElement[]> {
    const elements = await asyncForEach(
      [
        ...SYSTEM_PATIENT_PROFILE_METADATA_DISPLAYS.filter((display) =>
          display.displayTargets.includes(displayTarget)
        ),
        ...displays,
      ],
      async (display) => {
        const metadataConfig = SYSTEM_METADATA_CONFIGURATIONS.find(
          (systemConfig) =>
            `${display.metadataKey}.`.startsWith(`${systemConfig.key}.`)
        );

        if (metadataConfig?.feature) {
          const isEnabled = await snapshot(
            this._featureFlags.isFeatureEnabled$(metadataConfig.feature)
          );
          if (!isEnabled) {
            return;
          }
        }

        const rawValue = Patient.getMetadata(patient, display.metadataKey);
        const format = metadataConfig?.type;

        if (display.onlyShowIfHasValue && !rawValue) {
          return;
        }

        return {
          display,
          value: this._getFormattedValue(rawValue, format),
        };
      }
    );

    return compact(elements);
  }

  private _getFormattedValue(
    value?: PatientMetadataValue,
    format?: PatientMetadataType
  ): PatientMetadataValue | undefined {
    if (isArray(value)) {
      return value
        .map((arrayValue) => this._getFormattedValue(arrayValue, format))
        .join(', ');
    }

    if (format === PatientMetadataType.Boolean) {
      return coerceBooleanProperty(value) === true ? 'Yes' : 'No';
    }

    if (format === PatientMetadataType.Number) {
      return !value ? 0 : toInt(value);
    }

    return value;
  }
}
